import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Check, Edit3, Eye } from "lucide-react";
import { useApp } from "../../context/formflow/AppContext";
import { ROLE_PALETTE } from "../../constants/formflow/fieldTypes";
import axiosInstance from "../../api/axiosInstance";

function getSubmissionStatus(recipients) {
  const totalRecipients = recipients.length;
  const completedCount = recipients.filter((r) => r.status === "signed" || r.status === "completed").length;
  const viewedCount = recipients.filter((r) => r.status === "viewed" || r.viewedAt).length;

  if (totalRecipients > 0 && completedCount === totalRecipients) return "completed";
  if (completedCount > 0) return "partially_filled";
  if (viewedCount > 0) return "viewed";
  return "sent";
}

export function SubmissionsPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useApp();
  const [expanded, setExpanded] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  const allSubmissions = useMemo(() => {
    const subs = [];
    forms.forEach((form) => {
      (form.submissions || []).forEach((env) => {
        const recipients = (env.signers || []).map((signer) => {
          const role = (form.roles || []).find((r) => r.id === signer.roleId);
          const roleFields = (form.fields || []).filter((f) => f.roleId === signer.roleId);
          return {
            id: signer.id,
            name: signer.name,
            email: signer.email,
            status: signer.status,
            roleId: signer.roleId,
            roleName: role?.name || "Unknown Role",
            token: signer.token,
            viewedAt: signer.viewed_at || null,
            submittedAt: signer.signed_at || null,
            envelopeId: signer.envelope_id || null,
            fieldCount: roleFields.length,
            submittedFields: {}, // TODO: load actual submitted field values
          };
        });

        subs.push({
          id: env.id,
          formId: form.id,
          formName: form.name,
          rawStatus: env.status,
          status: env.downloaded_at ? "downloaded" : getSubmissionStatus(recipients),
          sentAt: env.created_at,
          downloadedAt: env.downloaded_at || null,
          contactId: env.contact_id || null,
          contactName: env.contact_name || "",
          contactAddress: env.contact_address || "",
          roles: form.roles || [],
          fields: form.fields || [],
          file: form.file,
          recipients,
        });
      });
    });
    if (formId) {
      return subs.filter((s) => String(s.formId) === String(formId));
    }
    return subs;
  }, [forms, formId]);

  const groupedSubmissions = useMemo(() => {
    const groups = new Map();

    allSubmissions.forEach((sub) => {
      const key = sub.contactId || `submission-${sub.id}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          contactId: sub.contactId,
          contactName: sub.contactName,
          contactAddress: sub.contactAddress,
          submissions: [],
          latestSentAt: sub.sentAt,
        });
      }
      const group = groups.get(key);
      if (!group.contactName && sub.contactName) group.contactName = sub.contactName;
      if (!group.contactAddress && sub.contactAddress) group.contactAddress = sub.contactAddress;
      if (!group.latestSentAt || new Date(sub.sentAt) > new Date(group.latestSentAt)) group.latestSentAt = sub.sentAt;
      group.submissions.push(sub);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        submissions: group.submissions.sort((a, b) => new Date(b.sentAt || 0) - new Date(a.sentAt || 0)),
      }))
      .sort((a, b) => new Date(b.latestSentAt || 0) - new Date(a.latestSentAt || 0));
  }, [allSubmissions]);

  const stats = useMemo(() => {
    return {
      sent: allSubmissions.length,
      viewed: allSubmissions.filter((sub) => sub.status === "viewed").length,
      partiallyFilled: allSubmissions.filter((sub) => sub.status === "partially_filled").length,
      completed: allSubmissions.filter((sub) => sub.status === "completed" || sub.status === "downloaded").length,
      downloaded: allSubmissions.filter((sub) => sub.downloadedAt).length,
    };
  }, [allSubmissions]);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get("/api/formflow/forms?limit=1000");
      if (!data.success) {
        showToast("Failed to load forms", "error");
        return;
      }
      setForms(data.forms || []);
    } catch (err) {
      console.error(err);
      showToast("Error loading forms", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const statusC = {
    draft: { bg: "#f3f4f6", text: "#6b7280" },
    sent: { bg: "#dbeafe", text: "#1e40af" },
    viewed: { bg: "#e0e7ff", text: "#3730a3" },
    partially_filled: { bg: "#fef3c7", text: "#92400e" },
    completed: { bg: "#d1fae5", text: "#065f46" },
    downloaded: { bg: "#ede9fe", text: "#5b21b6" },
    declined: { bg: "#fee2e2", text: "#991b1b" },
  };

  const statCards = [
    { label: "Sent", value: stats.sent, color: "#10b981" },
    { label: "Viewed", value: stats.viewed, color: "#3b82f6" },
    { label: "Filled Partially", value: stats.partiallyFilled, color: "#f59e0b" },
    { label: "Completed", value: stats.completed, color: "#22c55e" },
    { label: "Downloaded", value: stats.downloaded, color: "#8b5cf6" },
  ];

  if (loading) {
    return <div className="min-h-screen bg-slate-50 p-8 text-center">Loading...</div>;
  }

  return (
    <div className="bg-slate-50">
      <div className="min-h-screen bg-slate-50 p-8 max-w-7xl mx-auto">
        <button onClick={() => navigate("/formflow")} className="flex items-center gap-1.5 border-none bg-transparent cursor-pointer text-gray-500 text-sm mb-5 py-1.5">
          <ArrowLeft size={16} /> Back
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1.5">Submissions Sent</h1>
        <p className="text-gray-500 mb-7">
          {allSubmissions.length} submission{allSubmissions.length !== 1 ? "s" : ""}
          {formId ? " for selected template" : " grouped by contact"}
        </p>

        <div className="grid grid-cols-5 gap-5 mb-7">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-5.5 shadow-sm border border-gray-100 flex items-center gap-4.5">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl font-bold"
                style={{
                  background: s.color + "18",
                  color: s.color,
                }}
              >
                {s.value}
              </div>
              <div className="text-sm text-gray-500 font-medium">{s.label}</div>
            </div>
          ))}
        </div>

        {allSubmissions.length === 0 ? (
          <div className="text-center p-16 bg-white rounded-2xl border-2 border-dashed border-gray-200">
            <div className="text-5xl mb-3.5">📬</div>
            <h3 className="text-gray-700 mb-2">No submissions yet</h3>
            <p className="text-gray-400 m-0">{formId ? "This form has no submissions" : "Send a form to see submissions here"}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {groupedSubmissions.map((group) => {
              const groupExpanded = !!expandedGroups[group.key];
              const groupCounts = group.submissions.reduce((acc, sub) => {
                acc[sub.status] = (acc[sub.status] || 0) + 1;
                return acc;
              }, {});

              return (
              <div key={group.key} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div onClick={() => setExpandedGroups((prev) => ({ ...prev, [group.key]: !prev[group.key] }))} className="px-5 py-4 bg-slate-50 border-b border-gray-100 flex items-center justify-between gap-4 cursor-pointer">
                  <div>
                    <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1">Contact</div>
                    <div className="text-sm font-bold text-gray-900">{group.contactName || group.contactId || "No Contact"}</div>
                    <div className="text-xs text-gray-500">{group.contactAddress || (group.contactId ? `ID: ${group.contactId}` : "No contact information saved")}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {["sent", "viewed", "partially_filled", "completed", "downloaded"].map((status) => {
                      const sc = statusC[status] || statusC.sent;
                      return (
                        <span key={status} title={status.replace(/_/g, " ")} className="min-w-7 text-center text-[11px] font-bold px-2 py-1 rounded-full border" style={{ background: sc.bg, color: sc.text, borderColor: sc.text + "33" }}>
                          {groupCounts[status] || 0}
                        </span>
                      );
                    })}
                    <ChevronDown size={16} className="text-gray-400 transition-transform duration-200" style={{ transform: groupExpanded ? "rotate(180deg)" : "none" }} />
                  </div>
                </div>

                {groupExpanded && <div className="flex flex-col gap-3.5 p-4">
                  {group.submissions.map((sub) => {
                    const sc = statusC[sub.status] || statusC.sent;
                    const isExp = expanded === sub.id;
                    const completedCount = sub.recipients.filter((r) => r.status === "signed" || r.status === "completed").length;
                    const totalRecipients = sub.recipients.length;
                    const progressPercent = totalRecipients === 0 ? 0 : (completedCount / totalRecipients) * 100;

                    return (
                      <div key={sub.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                        <div onClick={() => setExpanded(isExp ? null : sub.id)} className="p-4 cursor-pointer flex items-center gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2.5 mb-1.5">
                              <h3 className="text-sm font-bold text-gray-900 m-0">{sub.formName}</h3>
                              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.text }}>
                                {sub.status?.replace(/_/g, " ") || "sent"}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              Sent {new Date(sub.sentAt).toLocaleDateString()} · {completedCount}/{totalRecipients} completed
                            </div>
                          </div>
                          <div className="w-24">
                            <div className="h-1.5 bg-gray-100 rounded overflow-hidden mb-1">
                              <div
                                className="h-full rounded transition-all duration-300"
                                style={{
                                  width: `${progressPercent}%`,
                                  background: sub.status === "completed" ? "#10b981" : "#f59e0b",
                                }}
                              />
                            </div>
                            <div className="text-xs text-gray-400 text-right">{Math.round(progressPercent)}%</div>
                          </div>
                          <ChevronDown size={16} className="text-gray-400 transition-transform duration-200" style={{ transform: isExp ? "rotate(180deg)" : "none" }} />
                        </div>

                        {isExp && (
                          <div className="border-t border-gray-100 px-5 pb-5 pt-1">
                            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mt-3.5 mb-2.5">Recipients</div>
                            <div className="flex flex-col gap-2">
                              {sub.recipients.map((rec) => {
                                const roleIndex = sub.roles.findIndex((r) => r.id === rec.roleId);
                                const rc = ROLE_PALETTE[Math.max(0, roleIndex) % ROLE_PALETTE.length];
                                const isCompleted = rec.status === "completed" || rec.status === "signed";
                                const isViewed = rec.status === "viewed" && !isCompleted;
                                return (
                                  <div key={rec.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0" style={{ background: rc.bg, color: rc.text }}>
                                      {rec.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1">
                                      <div className="text-[13px] font-semibold text-gray-900">{rec.name}</div>
                                      <div className="text-xs text-gray-500 flex gap-2">
                                        <span>{rec.email}</span>
                                        <span>·</span>
                                        <span style={{ color: rc.dot, fontWeight: 600 }}>{rec.roleName}</span>
                                        <span>·</span>
                                        <span>{rec.fieldCount} fields</span>
                                      </div>
                                    </div>
                                    {isCompleted ? (
                                      <div className="flex items-center gap-1 text-green-600 text-xs font-semibold">
                                        <Check size={15} />
                                        {rec.submittedAt ? new Date(rec.submittedAt).toLocaleDateString() : "Completed"}
                                        <button onClick={() => navigate(`/formflow/envelope/${rec.envelopeId}`)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1 hover:bg-red-700 transition">
                                          <Edit3 size={12} /> View
                                        </button>
                                      </div>
                                    ) : isViewed ? (
                                      <div className="flex items-center gap-1 text-yellow-600 text-xs font-semibold">
                                        <Eye size={15} />
                                        {rec.viewedAt ? new Date(rec.viewedAt).toLocaleDateString() : "Viewed"}
                                        <button onClick={() => navigate(`/formflow/sign/${rec.token}`)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1 hover:bg-red-700 transition">
                                          <Edit3 size={12} /> Fill Form
                                        </button>
                                      </div>
                                    ) : (
                                      <button onClick={() => navigate(`/formflow/sign/${rec.token}`)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1 hover:bg-red-700 transition">
                                        <Edit3 size={12} /> Fill Form
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            <div className="mt-4 bg-gray-50 rounded-lg p-3.5 border border-gray-100">
                              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2.5">Contact Info</div>
                              <div className="text-sm text-gray-700 font-semibold">{sub.contactName || sub.contactId || "No Contact"}</div>
                              <div className="text-xs text-gray-500 mt-1">{sub.contactAddress || (sub.contactId ? `ID: ${sub.contactId}` : "No contact information saved")}</div>
                            </div>

                            {/* Submitted data section (unchanged) */}
                            {sub.recipients.some((r) => r.status === "signed" || r.status === "completed") && sub.recipients.some((r) => Object.keys(r.submittedFields || {}).length > 0) && (
                              <div className="mt-4 bg-gray-50 rounded-lg p-3.5">
                                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2.5">Submitted Data</div>
                                {sub.recipients
                                  .filter((r) => r.status === "signed" || r.status === "completed")
                                  .map((rec) => {
                                    const fieldsObj = rec.submittedFields || {};
                                    if (Object.keys(fieldsObj).length === 0) return null;
                                    return (
                                      <div key={rec.id} className="mb-3">
                                        <div className="text-xs font-bold text-gray-700 mb-1.5">{rec.name}:</div>
                                        <div className="flex flex-wrap gap-1.5">
                                          {Object.entries(fieldsObj).map(([fieldId, val]) => {
                                            const field = sub.fields.find((f) => String(f.id) === String(fieldId));
                                            if (!field) return null;
                                            let displayVal = val;
                                            if (val === true) displayVal = "✓ Yes";
                                            else if (val === false) displayVal = "✗ No";
                                            else if (typeof val === "string" && val.startsWith("data:")) displayVal = "[Signature]";
                                            else displayVal = String(val || "");
                                            return (
                                              <div key={fieldId} className="px-2 py-1 bg-gray-200 rounded text-xs text-gray-700">
                                                <span className="text-gray-500">{field.label}: </span>
                                                <b>{displayVal}</b>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>}
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
